import { useMemo } from "react";
import { captureException } from "@sentry/react";
import { z } from "zod";
import useLandingPageIds from "../../../hooks/useLandingPageIds";
import { useSafeSessionStorageState } from "../../../hooks/useSafeStorageState";

export const STORAGE_KEY_PREFIX = "prescreening_";

export const getStorageKey = (landingPageId: string, suffix: string = "") =>
  `${STORAGE_KEY_PREFIX}${landingPageId}${suffix}`;

// Define zod schema for answers
export const answersSchema = z
  .record(z.union([z.string(), z.array(z.string())]))
  .default({});

// Define zod schema for the rest of the state
export const prescreeningMetaStateSchema = z.object({
  currentQuestionIndex: z.number().default(0),
  isDisqualified: z.boolean().default(false),
  bmi: z.number().nullable().default(null),
  error: z.string().optional(),
  disqualifyMessage: z.string().nullish(),
});

// Type for answers record
export type AnswersState = z.infer<typeof answersSchema>;

// Type for meta state
export type PrescreeningMetaState = z.infer<typeof prescreeningMetaStateSchema>;

// Combined state for backwards compatibility
export type PrescreeningState = PrescreeningMetaState & {
  answers: AnswersState;
};

const DEFAULT_ANSWERS: AnswersState = {};

const DEFAULT_META_STATE: PrescreeningMetaState = {
  currentQuestionIndex: 0,
  isDisqualified: false,
  bmi: null,
  disqualifyMessage: null,
};

/**
 * Custom hook to manage prescreening questionnaire state with localStorage
 * This handles transient state that is cleared upon successful submission
 *
 * @returns Object with state values and update functions
 */
export function usePrescreeningState() {
  const { variantId, masterId } = useLandingPageIds();

  // Create a unique composite key
  if (!masterId && !variantId) {
    throw new Error(
      "Invariant violation: both masterId and variantId are undefined",
    );
  }

  const uniqueKey = `${variantId}_${masterId}`;
  const answersKey = getStorageKey(uniqueKey, "_answers");
  const metaStateKey = getStorageKey(uniqueKey, "_meta");

  // Separate storage for answers
  const [answers, setAnswers] = useSafeSessionStorageState<AnswersState>(
    answersKey,
    {
      defaultValue: DEFAULT_ANSWERS,
      onError: (error) => {
        captureException(error, {
          tags: { validation_error: "prescreening_answers" },
        });
      },
      listenStorageChange: true,
    },
  );

  // Separate storage for meta state
  const [metaState, setMetaState] =
    useSafeSessionStorageState<PrescreeningMetaState>(metaStateKey, {
      defaultValue: DEFAULT_META_STATE,
      onError: (error) => {
        captureException(error, {
          tags: { validation_error: "prescreening_meta" },
        });
      },
      listenStorageChange: true,
    });

  // Ensure we never return undefined state
  const safeAnswers = answers || DEFAULT_ANSWERS;
  const safeMetaState = metaState || DEFAULT_META_STATE;

  const updateAnswers = (newAnswers: AnswersState) => {
    setAnswers(answersSchema.parse(newAnswers));
  };

  const updateQuestionnareState = (updates: Partial<PrescreeningMetaState>) => {
    setMetaState((prevState) => {
      const currentState = prevState || DEFAULT_META_STATE;
      const newState = { ...currentState, ...updates };
      return prescreeningMetaStateSchema.parse(newState);
    });
  };

  const clearState = () => {
    setAnswers(DEFAULT_ANSWERS);
    setMetaState(DEFAULT_META_STATE);
  };

  return useMemo(
    () => ({
      questionnaireState: {
        ...safeMetaState,
        answers: safeAnswers,
      },
      updateAnswers,
      updateQuestionnareState,
      clearState,
    }),
    [safeMetaState, safeAnswers],
  );
}
